import classNames from 'classnames';
import React from 'react';

import {LocalizedHtml, useI18n} from '@udemy/i18n';
import {Icon, IconName} from '@udemy/icons';
import {TextSkeleton} from '@udemy/react-reveal-components';
import {Price} from '@udemy/shopping';
import {SubscriptionDiscountRibbon} from '@udemy/subscription-browse';
import {ConsumerSubscriptionPlan, DateIntervalType} from '@udemy/subscription-graphql';
import {useFormatCurrency} from '@udemy/ud-data';

import {usePersonalPlanSubscriptionPricingReady} from '../../hooks/usePersonalPlanSubscriptionPricingReady';
import {
    useNonCatalogCheckoutSource,
    usePersonalPlanSubscriptionCheckoutEntry,
} from '../../personal-plan-subscription-checkout-entry-context';
import {isThreeMonthRenewalInterval} from '../../utils/personal-plan-renewal-interval';
import {PersonalPlanSubscriptionCTA} from '../personal-plan-subscription-cta/personal-plan-subscription-cta.react-component';
import styles from './personal-plan-cards.module.less';
import {PlanCard} from './plan-card.react-component';

export interface PersonalPlanCardsProps {
    plan?: ConsumerSubscriptionPlan;
    isCoursesPriceRangeLoading?: boolean;
    coursesPriceRange?: {
        min_price: Price;
        max_price: Price;
    };
}

/** Renders a feature icon with the same treatment as the Starter Plan cards. */
const featureIcon = (glyph: IconName) => <Icon glyph={glyph} label={false} size="small" />;

/**
 * ### PersonalPlanCards
 *
 * Plan comparison section for the revamped Personal Plan landing page. It uses the
 * Starter Plan landing page card layout so both pages look the same.
 *
 */
export const PersonalPlanCards = ({
    plan,
    isCoursesPriceRangeLoading,
    coursesPriceRange,
}: PersonalPlanCardsProps) => {
    const {gettext, interpolate} = useI18n();
    const {formatCurrency} = useFormatCurrency();
    const subscriptionCheckoutEntry = usePersonalPlanSubscriptionCheckoutEntry();
    const {source: nonCatalogSource, noticeDataLoading: nonCatalogNoticeDataLoading} =
        useNonCatalogCheckoutSource();
    const isSubscriptionPricingReady = usePersonalPlanSubscriptionPricingReady();

    const priceSkeleton = <TextSkeleton lineCountPerParagraph={1} paragraphCount={1} />;

    // The leading phrase of each entry is bold via the `feature-lead` class.
    // LocalizedHtml matches `class="lead"` in the string and swaps in our styled element.
    const subscriptionFeatures = [
        {
            icon: featureIcon('tick'),
            text: gettext(
                '<span class="lead">Explore thousands of expert-led courses</span> across AI, tech, business &amp; more — with new content added every month',
            ),
        },
        {
            icon: featureIcon('tick'),
            text: gettext(
                '<span class="lead">Know where to focus</span> with over 600 skills assessments',
            ),
        },
        {
            icon: featureIcon('tick'),
            text: gettext(
                '<span class="lead">Build skills by doing</span> with 180+ labs, coding exercises &amp; 1800+ AI Role Plays',
            ),
        },
        {
            icon: featureIcon('tick'),
            text: gettext(
                '<span class="lead">Prepare for certifications</span> with over 12000 cert prep courses and practice tests.',
            ),
        },
    ].map(({icon, text}) => ({
        icon,
        text: (
            <LocalizedHtml
                html={text}
                interpolate={{lead: <span className={styles['feature-lead']} />}}
            />
        ),
    }));

    const marketplaceFeatures = [
        {icon: featureIcon('tick'), text: gettext('Lifetime access to one course')},
        {icon: featureIcon('block'), text: gettext('No skill assessments')},
        {icon: featureIcon('block'), text: gettext('No labs')},
    ];

    // Same fields as PersonalPlanChecklist: selected option's monthly equivalent
    // (often annual billed as $/mo) plus that option's discount.
    const selectedDiscount = plan?.selectedDiscount;
    const selectedListPriceText = plan?.formattedMonthlyAmount;
    const selectedDiscountPriceText = selectedDiscount
        ? formatCurrency(selectedDiscount.subscriptionDiscountMonthlyPrice)
        : undefined;

    // Renders "Starting at $X/mo" for the selected plan option.
    const renderSubscriptionPrice = () => {
        if (!isSubscriptionPricingReady) {
            return priceSkeleton;
        }
        if (!plan || !('id' in plan)) {
            return undefined;
        }
        if (!selectedListPriceText && !selectedDiscountPriceText) {
            return undefined;
        }

        // One translatable string so translators can reorder around the price.
        // Strikethrough list price stays inside %(price)s so it does not split the sentence.
        const currentPriceText = selectedDiscountPriceText ?? selectedListPriceText ?? '';
        const priceMarkup =
            selectedDiscountPriceText && selectedListPriceText
                ? `<span class="list">${selectedListPriceText}</span> ${selectedDiscountPriceText}`
                : currentPriceText;

        return (
            <span
                className={styles['subscription-price']}
                data-purpose="subscription-text"
                data-testid="subscription-price"
            >
                <LocalizedHtml
                    html={interpolate(
                        gettext('Starting at %(price)s/mo'),
                        {price: priceMarkup},
                        true,
                    )}
                    interpolate={{
                        list: <span className={styles['price-strikethrough']} />,
                    }}
                />
            </span>
        );
    };

    const renderDiscountRibbon = () => {
        if (!selectedDiscount) {
            return undefined;
        }
        return (
            <div className={styles['discount-ribbon']}>
                <SubscriptionDiscountRibbon subscriptionDiscount={selectedDiscount} />
            </div>
        );
    };

    const marketplacePrice = isCoursesPriceRangeLoading ? (
        priceSkeleton
    ) : coursesPriceRange ? (
        <span data-purpose="marketplace-text">
            {`${formatCurrency(coursesPriceRange.min_price.amount, {
                currency: coursesPriceRange.min_price.currency,
                symbol: coursesPriceRange.min_price.currency_symbol,
            })}-${formatCurrency(coursesPriceRange.max_price.amount, {
                currency: coursesPriceRange.max_price.currency,
                symbol: coursesPriceRange.max_price.currency_symbol,
            })}`}
        </span>
    ) : undefined;

    // Quarterly offers (3-month renewal) use the same short line as the checklist.
    // Every other offer keeps the monthly-or-annual line.
    const subscriptionPriceDetails = plan
        ? isThreeMonthRenewalInterval(plan.monthlyPlanOption?.renewalInterval)
            ? gettext('Cancel anytime.')
            : gettext('Billed monthly or annually. Cancel anytime.')
        : undefined;

    const subscriptionCta = (planPreference: DateIntervalType) => (
        <PersonalPlanSubscriptionCTA
            plan={plan}
            planPreference={planPreference}
            uiRegion="personal_plan_cards"
            showSubtitle={false}
            subscriptionCheckoutEntry={subscriptionCheckoutEntry}
            source={nonCatalogSource}
            noticeDataLoading={nonCatalogNoticeDataLoading}
        />
    );

    return (
        <div className={styles['wrapper']}>
            <div className="ud-container">
                <h2 className={classNames('ud-heading-xxl', styles['heading'])}>
                    {gettext('Choose a plan that works for you')}
                </h2>
                <div className={styles['cards']}>
                    <PlanCard
                        highlighted={true}
                        isRecommended={true}
                        recommendedLabel={gettext('Best value')}
                        title={
                            <>
                                {gettext('Personal')}{' '}
                                <span className={styles['title-accent']}>{gettext('Plan')}</span>
                            </>
                        }
                        subtitle={gettext('For continuous skill building')}
                        features={subscriptionFeatures}
                        priceBadge={renderDiscountRibbon()}
                        priceComponent={renderSubscriptionPrice()}
                        priceDetails={subscriptionPriceDetails}
                        stackPriceDetails={true}
                        cta={subscriptionCta(DateIntervalType.Month)}
                    />
                    <PlanCard
                        title={gettext('Buy individual courses')}
                        subtitle={gettext('Learn anything')}
                        features={marketplaceFeatures}
                        priceComponent={marketplacePrice}
                        priceDetails={gettext('One-time purchase.')}
                    />
                </div>
            </div>
        </div>
    );
};
